import TechChecklist from "../../components/TechChecklist";
import NoteAssist from "../../components/NoteAssist";
import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import OnTheWayDialog from "../../components/OnTheWayDialog";
import JobMessageDialog from "../../components/JobMessageDialog";
import { callCustomer } from "../../lib/phone";
import api, { formatTL, formatDateTime, STATUS_LABELS } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ArrowLeft, MapPin, Phone, Camera, FileDown, CheckCircle, Plus, Trash2, PenTool, Truck, Play, ChevronRight, XCircle, QrCode } from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { generateServicePDF } from "../../lib/pdfGenerator";
import QrSticker from "../../components/QrSticker";

export default function TechJobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [parts, setParts] = useState([]);
  const beforeFileRef = useRef(null);
  const afterFileRef = useRef(null);
  // Tamamlama akışı
  const [signOpen, setSignOpen] = useState(false);
  const [otwOpen, setOtwOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [signStep, setSignStep] = useState("parts"); // parts | customer | tech
  const [partsMode, setPartsMode] = useState(null); // null | "none" | "yes"
  const customerSigRef = useRef(null);
  const techSigRef = useRef(null);
  const [completing, setCompleting] = useState(false);
  const [customerSigDataUrl, setCustomerSigDataUrl] = useState("");

  const load = async () => {
    const [j, p] = await Promise.all([api.get(`/jobs/${id}`), api.get("/parts")]);
    setJob(j.data); setParts(p.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!job) return <div className="text-center py-20 text-slate-500">Yükleniyor...</div>;

  const update = (k, v) => setJob({ ...job, [k]: v });

  const addPart = () => setJob({ ...job, parts_used: [...(job.parts_used || []), { part_id: "", name: "", qty: 1, unit_price: 0, total: 0 }] });
  const removePart = (i) => {
    const u = [...job.parts_used]; u.splice(i, 1);
    setJob({ ...job, parts_used: u });
  };
  const updatePart = (i, k, v) => {
    const u = [...job.parts_used];
    u[i] = { ...u[i], [k]: v };
    if (k === "part_id") {
      const p = parts.find(p => p.id === v);
      if (p) { u[i].name = p.name; u[i].unit_price = p.price; }
    }
    u[i].total = (Number(u[i].qty) || 0) * (Number(u[i].unit_price) || 0);
    setJob({ ...job, parts_used: u });
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    // Content-Type ayarlamıyoruz — axios/browser boundary'li olarak otomatik ekler
    const { data } = await api.post("/upload", fd);
    const token = localStorage.getItem("token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}?auth=${token}`;
  };

  const handlePhoto = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      toast.info("Yükleniyor...");
      const url = await upload(file);
      const list = [...(job[kind] || []), url];
      setJob({ ...job, [kind]: list });
      toast.success("Fotoğraf yüklendi");
    } catch { toast.error("Yükleme başarısız"); }
  };

  const removePhoto = (kind, idx) => {
    const list = [...(job[kind] || [])]; list.splice(idx, 1);
    setJob({ ...job, [kind]: list });
  };

  const saveProgress = async (status = null, silent = false) => {
    const payload = {
      work_done: job.work_done,
      parts_used: job.parts_used,
      labor_fee: Number(job.labor_fee) || 0,
      photos_before: job.photos_before,
      photos_after: job.photos_after,
    };
    if (status) payload.status = status;
    try {
      const { data } = await api.put(`/jobs/${id}`, payload);
      setJob(data);
      if (!status && !silent) toast.success("Kaydedildi");
      return data;
    } catch (e) {
      console.error("[saveProgress]", e.response?.data, e);
      if (!silent) toast.error(e.response?.data?.detail || "Kayıt hatası");
      return null;
    }
  };

  const startCompletion = () => {
    if (!job.work_done) { toast.error("Önce 'Yapılan İş' alanını doldurun"); return; }
    setPartsMode(job.parts_none ? "none" : (job.parts_used || []).length ? "yes" : null);
    setSignStep("parts");
    setSignOpen(true);
    setCustomerSigDataUrl("");
    setTimeout(() => customerSigRef.current?.clear(), 100);
  };

  const chooseParts = (mode) => {
    setPartsMode(mode);
    if (mode === "none") setJob({ ...job, parts_used: [], parts_none: true });
    else if (!(job.parts_used || []).length) addPart();
  };

  const proceedFromParts = () => {
    if (!partsMode) { toast.error("Parça kullanıldı mı? Bir seçim yapın"); return; }
    if (partsMode === "yes") {
      const list = (job.parts_used || []).filter(p => (p.name || "").trim());
      if (!list.length) { toast.error("En az bir parça girin ya da 'Parça kullanmadım' seçin"); return; }
      if (list.some(p => !(Number(p.qty) > 0))) { toast.error("Parça adedi 0'dan büyük olmalı"); return; }
      setJob({ ...job, parts_used: list, parts_none: false });
    }
    setSignStep("customer");
    setTimeout(() => customerSigRef.current?.clear(), 100);
  };

  const proceedToTechSign = () => {
    if (customerSigRef.current?.isEmpty()) {
      toast.error("Müşteri imzası alın");
      return;
    }
    // İmzayı hemen kaydet; sonra canvas unmount olabilir
    try {
      const dataUrl = customerSigRef.current.toDataURL("image/png");
      setCustomerSigDataUrl(dataUrl);
    } catch (e) {
      console.error("[sign] müşteri imza alınamadı:", e);
      toast.error("Müşteri imzası okunamadı, tekrar deneyin");
      return;
    }
    setSignStep("tech");
    setTimeout(() => techSigRef.current?.clear(), 100);
  };

  const finalizeJob = async () => {
    if (techSigRef.current?.isEmpty()) {
      toast.error("Teknisyen imzası alın");
      return;
    }
    setCompleting(true);

    // 1) Müşteri imza yükle (dataUrl state'te saklanıyor çünkü canvas unmount oldu)
    let custUrl = "";
    try {
      if (!customerSigDataUrl) throw new Error("Müşteri imza verisi yok");
      const custBlob = await (await fetch(customerSigDataUrl)).blob();
      const custFile = new File([custBlob], "musteri-imza.png", { type: "image/png" });
      custUrl = await upload(custFile);
    } catch (err) {
      console.error("[finalize] müşteri imza upload:", err);
      toast.error("Müşteri imzası yüklenemedi — bağlantıyı kontrol edin");
      setCompleting(false);
      return;
    }

    // 2) Teknisyen imza yükle
    let techUrl = "";
    try {
      const techDataUrl = techSigRef.current.toDataURL("image/png");
      const techBlob = await (await fetch(techDataUrl)).blob();
      const techFile = new File([techBlob], "tek-imza.png", { type: "image/png" });
      techUrl = await upload(techFile);
    } catch (err) {
      console.error("[finalize] teknisyen imza upload:", err);
      toast.error("Teknisyen imzası yüklenemedi — bağlantıyı kontrol edin");
      setCompleting(false);
      return;
    }

    // 3) TEK PUT — tüm alanlar + status=tamamlandi
    let updated;
    try {
      const payload = {
        work_done: job.work_done || "",
        parts_used: partsMode === "none" ? [] : (job.parts_used || []),
        parts_none: partsMode === "none",
        labor_fee: Number(job.labor_fee) || 0,
        photos_before: job.photos_before || [],
        photos_after: job.photos_after || [],
        signature_url: custUrl,
        tech_signature_url: techUrl,
        status: "tamamlandi",
      };
      const res = await api.put(`/jobs/${id}`, payload);
      updated = res.data;
      setJob(updated);
      setSignOpen(false);
      toast.success("Servis tamamlandı ✓");
    } catch (err) {
      console.error("[finalize] status update:", err.response?.data, err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg).join(", ")
        : "Tamamlama başarısız — sunucuya ulaşılamadı";
      toast.error(msg);
      setCompleting(false);
      return;
    }

    // 4) PDF
    try {
      await generateServicePDF(updated);
      toast.success("PDF indirildi");
    } catch (err) {
      console.error("[finalize] pdf:", err);
      toast.error("PDF oluşturulamadı — 'PDF' butonundan tekrar deneyebilirsiniz");
    }
    setCompleting(false);
    setQrOpen(true);   // iş sonu: cihaz QR etiketi (sticker) yazdırma
  };

  const cancelJob = async () => {
    const reason = window.prompt("Müşteri vazgeçti sebebi (zorunlu):");
    if (!reason || !reason.trim()) return;
    try {
      const { data } = await api.put(`/jobs/${id}`, {
        status: "iptal",
        cancel_reason: reason.trim()
      });
      setJob(data);
      toast.success("İptal edildi. Admin panelinde görüntülenecek.");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const partsTotal = (job.parts_used || []).reduce((s, p) => s + (Number(p.total) || 0), 0);
  const total = partsTotal + (Number(job.labor_fee) || 0);

  return (
    <div className="space-y-4" data-testid="tech-job-detail">
      <Link to="/tech" className="inline-flex items-center gap-2 text-sm text-slate-600">
        <ArrowLeft className="h-4 w-4" /> Görevler
      </Link>

      {["atandi", "yolda", "basladi", "yeni"].includes(job.status) && (
        <div className="grid grid-cols-2 gap-2">
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setOtwOpen(true)} data-testid="ontheway-button">Yoldayım</Button>
          <Button variant="outline" onClick={() => setMsgOpen(true)} data-testid="msg-button">Müşteriye mesaj</Button>
        </div>
      )}
      <Link to="/tech/diagnosis" state={{ job }} className="block text-center text-sm font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-lg py-2" data-testid="diag-link">Arıza Asistanı</Link>

      <Card className="p-4 border border-slate-200 shadow-none">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-mono text-xs text-blue-700 font-bold">{job.job_number}</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-1">{job.customer_name}</div>
          </div>
          <span className={`status-pill border ${STATUS_LABELS[job.status]?.color}`}>{STATUS_LABELS[job.status]?.label}</span>
        </div>
        <div className="mt-3 space-y-2">
          <button type="button" onClick={() => callCustomer(job)} className="flex items-center gap-2 text-sm text-blue-700 hover:underline" data-testid="call-customer">
            <Phone className="h-4 w-4" /> {job.customer_phone || "Telefon belirtilmemiş"}{job.phone_masked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">gizli · aramak için dokunun</span>}
          </button>
          {job.customer_address ? (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(job.customer_address)}`} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-sm text-blue-700 hover:underline">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {job.customer_address}
            </a>
          ) : (
            <div className="flex items-start gap-2 text-sm text-slate-400 italic">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" /> Adres belirtilmemiş
            </div>
          )}
        </div>

        <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200 flex items-center gap-3">
          {job.device_brand_logo && <img src={job.device_brand_logo} alt="" className="h-8 w-8 object-contain bg-white rounded p-1 border border-slate-200" />}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Cihaz</div>
            {(job.device_brand_name || job.device_model) ? (
              <>
                <div className="font-semibold text-slate-900 text-sm">{job.device_brand_name} {job.device_model}</div>
                <div className="text-xs text-slate-500 capitalize">{job.device_type || "tip belirtilmemiş"} • {job.device_serial ? `S/N: ${job.device_serial}` : "S/N: -"}</div>
              </>
            ) : (
              <div className="text-sm text-slate-400 italic">Cihaz bilgisi belirtilmemiş</div>
            )}
          </div>
        </div>

        <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <div className="font-semibold text-xs uppercase tracking-wider mb-1">Arıza</div>
          {job.fault_description || <span className="italic text-amber-700/70">Arıza açıklaması belirtilmemiş</span>}
        </div>
      </Card>

      {/* Status flow buttons */}
      {job.status === "atandi" && (
        <Button onClick={() => saveProgress("yolda")} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="status-onway">
          <Truck className="h-4 w-4 mr-2" /> Yola Çık
        </Button>
      )}
      {job.status === "yolda" && (
        <Button onClick={() => saveProgress("basladi")} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="status-start">
          <Play className="h-4 w-4 mr-2" /> Servise Başla
        </Button>
      )}

      {(job.status === "basladi" || job.status === "tamamlandi" || job.status === "faturalandi") && (
        <>
          <TechChecklist job={job} disabled={job.status !== "basladi"} />
          <Card className="p-4 border border-slate-200 shadow-none space-y-3">
            <div>
              <Label>Yapılan İş</Label>
              <Textarea rows={3} data-testid="work-done-input" value={job.work_done || ""} onChange={(e) => update("work_done", e.target.value)} placeholder="Tespit ve müdahale notları..." />
              <NoteAssist value={job.work_done || ""} onChange={(v) => update("work_done", v)} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Yedek Parçalar</Label>
                <Button size="sm" variant="outline" onClick={addPart}><Plus className="h-3 w-3 mr-1" /> Ekle</Button>
              </div>
              <div className="text-xs text-slate-500 mb-2">"Manuel" seçerek stoğa kayıtlı olmayan parçaları da ekleyebilirsin.</div>
              {(job.parts_used || []).map((p, i) => (
                <div key={i} className="space-y-1 mb-3 p-2 rounded border border-slate-100 bg-slate-50/40">
                  <Select value={p.part_id || "manual"} onValueChange={(v) => updatePart(i, "part_id", v === "manual" ? "" : v)}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">✍️ Manuel parça gir</SelectItem>
                      {parts.map(pp => <SelectItem key={pp.id} value={pp.id}>{pp.name} (Stok: {pp.stock_qty})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Parça adı" value={p.name} onChange={(e) => updatePart(i, "name", e.target.value)} />
                  <div className="grid grid-cols-12 gap-1 items-center">
                    <Input className="col-span-3" type="number" placeholder="Adet" value={p.qty} onChange={(e) => updatePart(i, "qty", e.target.value)} />
                    <Input className="col-span-4" type="number" placeholder="Birim ₺" value={p.unit_price} onChange={(e) => updatePart(i, "unit_price", e.target.value)} />
                    <div className="col-span-4 text-right text-sm font-mono font-semibold">{formatTL(p.total)}</div>
                    <Button variant="ghost" size="sm" className="col-span-1 text-rose-600 h-8 px-1" onClick={() => removePart(i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
              <div><Label>İşçilik (₺)</Label><Input type="number" value={job.labor_fee || 0} onChange={(e) => update("labor_fee", e.target.value)} data-testid="tech-labor-input" /></div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Toplam</div>
                <div className="font-display text-xl font-bold text-slate-900">{formatTL(total)}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-slate-200 shadow-none">
            <Label>Fotoğraflar</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <div className="text-xs text-slate-500 mb-1">Önce</div>
                <div className="grid grid-cols-2 gap-1">
                  {(job.photos_before || []).map((u, i) => (
                    <div key={i} className="relative aspect-square rounded border border-slate-200 overflow-hidden">
                      <img src={u} alt="" className="object-cover w-full h-full" />
                      <button onClick={() => removePhoto("photos_before", i)} className="absolute top-1 right-1 bg-white/90 rounded p-0.5"><Trash2 className="h-3 w-3 text-rose-600" /></button>
                    </div>
                  ))}
                  <button onClick={() => beforeFileRef.current?.click()} className="aspect-square rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                    <Camera className="h-5 w-5" />
                  </button>
                  <input ref={beforeFileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handlePhoto(e, "photos_before")} />
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Sonra</div>
                <div className="grid grid-cols-2 gap-1">
                  {(job.photos_after || []).map((u, i) => (
                    <div key={i} className="relative aspect-square rounded border border-slate-200 overflow-hidden">
                      <img src={u} alt="" className="object-cover w-full h-full" />
                      <button onClick={() => removePhoto("photos_after", i)} className="absolute top-1 right-1 bg-white/90 rounded p-0.5"><Trash2 className="h-3 w-3 text-rose-600" /></button>
                    </div>
                  ))}
                  <button onClick={() => afterFileRef.current?.click()} className="aspect-square rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                    <Camera className="h-5 w-5" />
                  </button>
                  <input ref={afterFileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handlePhoto(e, "photos_after")} />
                </div>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-2 sticky bottom-20 z-30">
            <Button variant="outline" onClick={() => saveProgress()} data-testid="tech-save-button">Kaydet</Button>
            {job.status === "basladi" && (
              <Button onClick={startCompletion} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tech-complete-button">
                <CheckCircle className="h-4 w-4 mr-2" /> Tamamla
              </Button>
            )}
            {(job.status === "tamamlandi" || job.status === "faturalandi") && (
              <Button onClick={() => generateServicePDF(job)} className="bg-blue-600 hover:bg-blue-700" data-testid="tech-pdf-button">
                <FileDown className="h-4 w-4 mr-2" /> PDF
              </Button>
            )}
            {(job.status === "basladi" || job.status === "tamamlandi" || job.status === "faturalandi") && (
              <Button variant="outline" onClick={() => setQrOpen(true)} data-testid="tech-qr-button">
                <QrCode className="h-4 w-4 mr-2" /> QR Etiket
              </Button>
            )}
          </div>

          {(job.status === "basladi" || job.status === "yolda") && (
            <Button variant="outline" onClick={cancelJob} className="w-full text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="tech-cancel-button">
              <XCircle className="h-4 w-4 mr-2" /> Müşteri Vazgeçti
            </Button>
          )}
        </>
      )}

      <div className="text-xs text-slate-400 text-center pt-2">
        {job.completed_at && `Tamamlandı: ${formatDateTime(job.completed_at)}`}
      </div>

      {/* İmza akışı dialog */}
      <Dialog open={signOpen} onOpenChange={(o) => { if (!completing) setSignOpen(o); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-4 w-4" />
              {signStep === "parts" ? "1/3 — Yedek Parça" : signStep === "customer" ? "2/3 — Müşteri İmzası" : "3/3 — Teknisyen İmzası"}
            </DialogTitle>
          </DialogHeader>
          {signStep === "parts" ? (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="parts-step">
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={partsMode === "none" ? "default" : "outline"} onClick={() => chooseParts("none")} data-testid="parts-none-btn">Parça kullanmadım</Button>
                <Button type="button" variant={partsMode === "yes" ? "default" : "outline"} onClick={() => chooseParts("yes")} data-testid="parts-yes-btn">Parça kullandım</Button>
              </div>
              {partsMode === "yes" && (
                <>
                  {(job.parts_used || []).map((p, i) => {
                    const lp = parts.find(pp => pp.id === p.part_id)?.price;
                    const changed = p.part_id && lp !== undefined && Math.abs((Number(p.unit_price) || 0) - Number(lp)) > 0.005;
                    return (
                      <div key={i} className="space-y-1 p-2 rounded border border-slate-100 bg-slate-50/40" data-testid={`wiz-part-${i}`}>
                        <Select value={p.part_id || "manual"} onValueChange={(v) => updatePart(i, "part_id", v === "manual" ? "" : v)}>
                          <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual">✍️ Manuel parça gir</SelectItem>
                            {parts.map(pp => <SelectItem key={pp.id} value={pp.id}>{pp.name} (Stok: {pp.stock_qty})</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input placeholder="Parça adı" value={p.name} onChange={(e) => updatePart(i, "name", e.target.value)} />
                        <div className="grid grid-cols-12 gap-1 items-center">
                          <Input className="col-span-3" type="number" min="0" placeholder="Adet" value={p.qty} onChange={(e) => updatePart(i, "qty", e.target.value)} />
                          <Input className="col-span-4" type="number" min="0" placeholder="Birim ₺" value={p.unit_price} onChange={(e) => updatePart(i, "unit_price", e.target.value)} data-testid={`wiz-part-price-${i}`} />
                          <div className="col-span-4 text-right text-sm font-mono font-semibold">{formatTL(p.total)}</div>
                          <Button variant="ghost" size="sm" className="col-span-1 text-rose-600 h-8 px-1" onClick={() => removePart(i)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                        {changed && (
                          <div className="text-xs text-amber-700">Liste fiyatı {formatTL(lp)} — fiyatı değiştirdiniz.
                            <button type="button" className="underline ml-1" onClick={() => updatePart(i, "unit_price", lp)}>Listeye dön</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <Button size="sm" variant="outline" className="w-full" onClick={addPart}><Plus className="h-3 w-3 mr-1" /> Parça ekle</Button>
                </>
              )}
              <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
                <span className="text-slate-500">Parça {formatTL(partsMode === "none" ? 0 : partsTotal)} + işçilik {formatTL(Number(job.labor_fee) || 0)}</span>
                <span className="font-semibold">{formatTL((partsMode === "none" ? 0 : partsTotal) + (Number(job.labor_fee) || 0))}</span>
              </div>
            </div>
          ) : (
          <div className="space-y-3">
            <div className="p-2 rounded bg-slate-50 border border-slate-200 text-xs text-slate-600">
              {signStep === "customer"
                ? "Lütfen telefonu müşteriye verin ve aşağıdaki alana imza atmasını isteyin."
                : "Şimdi siz aşağıdaki alana imzanızı atın."}
            </div>
            <div className="border-2 border-dashed border-slate-300 rounded bg-white">
              {signStep === "customer" ? (
                <SignatureCanvas
                  ref={customerSigRef}
                  canvasProps={{ className: "w-full h-44", "data-testid": "customer-signature-pad" }}
                  penColor="#0f172a"
                />
              ) : (
                <SignatureCanvas
                  ref={techSigRef}
                  canvasProps={{ className: "w-full h-44", "data-testid": "tech-signature-pad" }}
                  penColor="#0f172a"
                />
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => signStep === "customer" ? customerSigRef.current?.clear() : techSigRef.current?.clear()}
            >
              Temizle
            </Button>
          </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" disabled={completing} onClick={() => setSignOpen(false)}>İptal</Button>
            {signStep === "parts" ? (
              <Button onClick={proceedFromParts} className="bg-blue-600 hover:bg-blue-700" data-testid="parts-next-button">
                İleri <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : signStep === "customer" ? (
              <Button onClick={proceedToTechSign} className="bg-blue-600 hover:bg-blue-700" data-testid="next-sign-button">
                İleri <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={finalizeJob} disabled={completing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finalize-job-button">
                {completing ? "İşleniyor..." : <><CheckCircle className="h-4 w-4 mr-2" /> Tamamla & PDF</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <OnTheWayDialog open={otwOpen} onOpenChange={setOtwOpen} job={job} onDone={() => load()} />
      <JobMessageDialog open={msgOpen} onOpenChange={setMsgOpen} job={job} />
    <QrSticker jobId={job.id} open={qrOpen} onOpenChange={setQrOpen} />
    </div>
  );
}
